import React from 'react';
import pluralize from 'pluralize';
import { getOrgTermsContent } from '../../content/utils.js';
import SubHeader from '../SubHeader.jsx';
import { PortfolioInfoLink } from '../links/index.jsx';
import VerticalSeparator from '../VerticalSeparator.jsx';
import { LeadIcon } from '../icons/LeadIcon.jsx';
import { ShowMoreItemsHeader } from '../projects/ChangeStrategy/ChangeStrategyOverview/ShowMoreItemsHeader.jsx';
import ProjectsTable from '../projects/ProjectsTable.jsx';
import PortfoliosActionDropdown from './PortfoliosActionDropdown.jsx';
import ToggleArrow from '../common/ToggleArrow.jsx';
import DateRangeDisplay from '../common/DateRangeDisplay.jsx';
import StatusDisplay from '../common/StatusDisplay.jsx';
import { useToggleSection } from '../../hooks/useToggleSection.js';
import {
  filterProjectsByIds,
  findStatus,
} from '../../containers/portfolio/utils/index.js';
import { INDENT } from '../../constants/index.js';

const PortfolioProjects = ({
  portfolio,
  organizationProjects,
  projectStatuses,
  onDeleteProject,
  onDeletePortfolio,
  queryState,
  indentLevel = 0,
  isTopLevel = false,
}) => {
  const {
    id,
    name,
    owners = [],
    projectStatusId,
    startDate,
    endDate,
    projects,
  } = portfolio;
  const { isHidden, className, toggle, isExpanded } =
    useToggleSection(isTopLevel);

  const portfolioProjectIds = projects ? projects.map((p) => p.id) : [];
  const portfolioProjects = filterProjectsByIds(
    organizationProjects,
    portfolioProjectIds
  );

  const status = findStatus(projectStatuses, projectStatusId);

  // Handle no_portfolio entity which only has id, projects, and permissions
  const displayName =
    id === 'no_portfolio' ? `No ${getOrgTermsContent('portfolio')}` : name;

  // Indentation values for nested portfolios
  const headerIndent = indentLevel > 0 ? indentLevel : 0;
  const contentIndent = indentLevel > 0 ? INDENT.PORTFOLIO_CONTENT : 0;
  const emptyMessagePadding = indentLevel > 0 ? INDENT.TOTAL_NESTED : 0;

  return (
    <section className={className}>
      <SubHeader
        flex
        className={className}
        medium
        style={headerIndent ? { paddingLeft: `${headerIndent}px` } : undefined}
      >
        <div className="flex-col w-100">
          <div className="m-b-18">
            <h3>
              <button
                className="btn-clear bg-transparent flex"
                onClick={toggle}
                type="button"
              >
                <ToggleArrow isExpanded={isExpanded} />
                <div className="m-l-16">
                  {id !== 'no_portfolio' ? (
                    <React.Fragment>
                      {getOrgTermsContent('portfolio')}:&nbsp;
                      {portfolio.permissions &&
                      portfolio.permissions.canRead ? (
                        <PortfolioInfoLink portfolioId={id}>
                          {displayName}
                        </PortfolioInfoLink>
                      ) : (
                        displayName
                      )}
                    </React.Fragment>
                  ) : (
                    displayName
                  )}
                </div>
              </button>
            </h3>
          </div>
          <div className="flex">
            {id !== 'no_portfolio' ? (
              <React.Fragment>
                <div className="flex header-item m-l-28">
                  <LeadIcon />
                  <strong className="text-muted m-r-12 m-l-8">
                    {pluralize('Owner', owners.length)}
                  </strong>
                  {owners.length ? (
                    <ShowMoreItemsHeader items={owners} maxShowNumber={2} />
                  ) : (
                    '—'
                  )}
                </div>
                <VerticalSeparator />
                <DateRangeDisplay startDate={startDate} endDate={endDate} />
                <VerticalSeparator />
              </React.Fragment>
            ) : null}
            <div className={id === 'no_portfolio' ? 'm-l-28' : 'm-l-18'}>
              <StatusDisplay status={status} />
            </div>
            <VerticalSeparator />
            <div className="flex header-item">
              <strong className="text-muted m-r-12">
                {getOrgTermsContent('projects')}:{' '}
              </strong>
              <strong>{projects ? projects.length || '—' : '—'}</strong>
            </div>
          </div>
        </div>

        {portfolio.permissions ? (
          <PortfoliosActionDropdown
            portfolioId={portfolio.id}
            portfolio={portfolio}
            onDeletePortfolio={onDeletePortfolio}
          />
        ) : null}
      </SubHeader>
      {!isHidden ? (
        portfolio.projects?.length ? (
          <ProjectsTable
            projects={portfolioProjects}
            onDeleteProject={onDeleteProject}
            statuses={projectStatuses}
            indentLevel={contentIndent}
          />
        ) : !queryState.search ? (
          <SubHeader
            bgWhite
            small
            flex
            style={
              emptyMessagePadding
                ? { paddingLeft: `${emptyMessagePadding}px`, minHeight: '60px' }
                : {}
            }
          >
            <strong>
              <h4 className="text-muted">
                {`There are no ${getOrgTermsContent('projects')} yet.`}
              </h4>
            </strong>
          </SubHeader>
        ) : null
      ) : null}
    </section>
  );
};

export default PortfolioProjects;
